import React from 'react';
import { Lightbulb } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel, TextInput } from '../../components/Primitives.jsx';
import { Segmented } from '../StepInspector.jsx';
import { MultiColsDropdown } from './MultiColsDropdown.jsx';
import { resolveLiveRows } from './columnClassification.js';
import { hasUpstreamSplit } from '../../data.js';
import { DecimalPlacesField } from '../shared/DecimalPlacesField.jsx';
import { OutputColumnNamesField } from '../shared/OutputColumnNamesField.jsx';

function computeIqrPreview(rows, selCols, k) {
  if (!rows || !rows.length || !selCols || !selCols.length) return null;
  const colName = selCols[0];
  if (!colName) return null;

  let trainRows = rows;
  for (const key of Object.keys(rows[0] || {})) {
    const vals = new Set(rows.map(r => r[key]).filter(v => v !== null && v !== ''));
    if (vals.size > 0 && Array.from(vals).every(v => v === 'train' || v === 'test')) {
      const filtered = rows.filter(r => r[key] === 'train');
      if (filtered.length > 0) trainRows = filtered;
      break;
    }
  }

  const nums = trainRows
    .map(r => {
      const v = r[colName];
      if (v === null || v === undefined || v === '') return NaN;
      return typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,]/g, ''));
    })
    .filter(v => !isNaN(v))
    .sort((a, b) => a - b);

  if (nums.length === 0) return null;

  const getPercentile = (p) => {
    const index = p * (nums.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index - lower;
    if (upper >= nums.length) return nums[nums.length - 1];
    return nums[lower] * (1 - weight) + nums[upper] * weight;
  };

  const q1 = getPercentile(0.25);
  const q3 = getPercentile(0.75);
  const iqr = q3 - q1;

  const lowVal = q1 - k * iqr;
  const highVal = q3 + k * iqr;

  let affectedCount = 0;
  for (const r of rows) {
    let isAffected = false;
    for (const col of selCols) {
      const val = r[col];
      if (val === null || val === undefined || val === '') continue;
      const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/[$,]/g, ''));
      if (!isNaN(num) && (num < lowVal || num > highVal)) {
        isAffected = true;
        break;
      }
    }
    if (isAffected) affectedCount++;
  }

  const affectedPct = Math.round((affectedCount / rows.length) * 1000) / 10;

  return {
    lowVal: Math.round(lowVal * 1000) / 1000,
    highVal: Math.round(highVal * 1000) / 1000,
    affectedCount,
    affectedPct
  };
}

export function IqrOutlierInspector({ step, onChange, pipeline, uploadedRows }) {
  const c = step.config || {};
  const sel = Array.isArray(c.columns) ? c.columns : (c.column ? [c.column] : []);
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;
  const rows = resolveLiveRows(uploadedRows, pipeline);

  const k = c.k ?? 1.5;
  const action = c.action || 'clip';

  const preview = computeIqrPreview(rows, sel, k);

  const applyPresetChip = (newK) => {
    u({ k: newK });
  };

  return (
    <div>
      <Eyebrow>IQR Method</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Cap or drop outliers using the Interquartile Range (IQR).
      </div>

      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          Warning: No Train / Test Split step detected upstream. If your end goal is machine learning modeling, please add a Train / Test Split block before performing output handling to avoid data leakage.
        </Alert>
      )}

      <MultiColsDropdown selected={sel} onChange={(v) => u({ columns: v, column: v[0] || null })} numeric />

      <div style={{ marginTop: 10 }}>
        <FieldLabel hint="Cap values outside bounds or drop outlier rows">Action on Outliers</FieldLabel>
        <Segmented
          value={action}
          onChange={(v) => u({ action: v })}
          options={[
            { value: 'clip', label: 'Clip (Cap values)' },
            { value: 'drop', label: 'Drop rows' }
          ]}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel hint="Quick selection for k multiplier">Presets</FieldLabel>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'Standard (1.5x)', val: 1.5 },
            { label: 'Moderate (2.0x)', val: 2.0 },
            { label: 'Extreme (3.0x)', val: 3.0 },
          ].map(p => {
            const active = k === p.val;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPresetChip(p.val)}
                style={{
                  background: active ? C.accentStrong : C.input,
                  color: active ? '#fff' : C.fg3,
                  border: `1px solid ${active ? C.accentStrong : C.borderStrong}`,
                  borderRadius: 6, padding: '4px 8px', fontSize: '.74rem',
                  fontWeight: 600, cursor: 'pointer', transition: 'all .15s'
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel hint="Multiplier for IQR">Multiplier (k): {k}</FieldLabel>
        <TextInput
          type="number"
          step="0.1"
          min="0"
          value={k}
          onChange={(v) => u({ k: parseFloat(v) || 0 })}
          style={{ width: 100 }}
        />
      </div>

      {preview && (
        <div style={{
          marginTop: 12, padding: '10px 12px',
          background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
          borderRadius: 8, fontSize: '.76rem', color: C.fg1, lineHeight: 1.5
        }}>
          <Lightbulb size={13} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} /> <strong>Preview:</strong> Based on the full dataset, an IQR multiplier of <strong>{k}</strong> {action === 'drop' ? 'drops' : 'clips'} values below <strong>{preview.lowVal}</strong> and above <strong>{preview.highVal}</strong>. This will affect <strong>{preview.affectedCount}</strong> rows (<strong>{preview.affectedPct}%</strong> of data).
        </div>
      )}

      {action !== 'drop' && (
        <OutputColumnNamesField
          columns={sel}
          value={c.output_columns}
          onChange={(v) => u({ output_columns: v })}
          legacyValue={c.output_column}
          autoName={(col) => `${col}_iqr`}
        />
      )}
      <DecimalPlacesField value={c.output_decimals} onChange={(v) => u({ output_decimals: v })} />
    </div>
  );
}

